import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DocumentDescriptionDTO } from '@iota/iota-management-portal-v4-client';
import { IotaModalOptions, IotaModalSize, MAT_DIALOG_DATA, MatDialogRef } from '@iota/iota-ui-components';
import { Store } from '@ngrx/store';
import { Observable, of, take } from 'rxjs';
import { DownloadDocument, LoadDocument } from 'src/app/store/actions/document.action';
import { documentByDocumentDescriptionId, DocumentDescriptionWithUrl } from 'src/app/store/reducer/document.reducer';
import { FeedbackBannerVariant } from '../../components/feedback-banner/feedback-banner.component';

export interface DialogShowDocumentData {
    document?: DocumentDescriptionDTO;
    blob?: Blob;
    fileName?: string;
    contentType?: string;
    showUploadButton?: boolean;
    feedbackBanner?: {
        variant?: FeedbackBannerVariant;
        header?: string;
        message?: string;
        date?: string;
        showCloseIcon?: boolean;
    };
}

@Component({
    standalone: false,
    selector: 'app-dialog-show-document',
    templateUrl: './dialog-show-document.component.html',
    styleUrls: ['./dialog-show-document.component.scss']
})
export class DialogShowDocumentComponent implements OnInit, OnDestroy {
    public modalOptions: Partial<IotaModalOptions> = {
        size: IotaModalSize.SIZE_3XL,
        showToggleFullScreenButton: true
    };

    constructor(
        public dialogRef: MatDialogRef<DialogShowDocumentComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogShowDocumentData,
        private store: Store,
        private sanitizer: DomSanitizer
    ) {}

    feedbackBannerClosed = false;

    document$: Observable<DocumentDescriptionWithUrl>;
    documentName: string;
    private blobObjectUrl: string;

    ngOnInit(): void {
        if (this.data.blob) {
            this.blobObjectUrl = URL.createObjectURL(this.data.blob);
            const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobObjectUrl);
            this.document$ = of({
                url: safeUrl,
                contentType: this.data.contentType,
                name: this.data.fileName
            } as DocumentDescriptionWithUrl);
            this.documentName = this.data.fileName;
            return;
        }

        const id = this.data.document.documentDescriptionId;
        this.document$ = this.store.select(documentByDocumentDescriptionId(id));
        this.documentName = this.data.document.name;

        this.store
            .select(documentByDocumentDescriptionId(id))
            .pipe(take(1))
            .subscribe((doc) => {
                if (!doc) {
                    this.store.dispatch(LoadDocument({ document: this.data.document }));
                }
            });
    }

    ngOnDestroy(): void {
        if (this.blobObjectUrl) {
            URL.revokeObjectURL(this.blobObjectUrl);
        }
    }

    closeFeedbackBanner() {
        this.feedbackBannerClosed = true;
    }

    close(): void {
        this.dialogRef.close();
    }

    isImage(): boolean {
        const contentType = this.data.document?.contentType ?? this.data.contentType;
        return !!contentType && contentType.toLowerCase().startsWith('image');
    }

    download() {
        if (this.data.blob) {
            const a = document.createElement('a');
            a.href = this.blobObjectUrl;
            a.download = this.data.fileName ?? 'document';
            a.click();
            return;
        }

        this.store.dispatch(DownloadDocument({ document: this.data.document }));
    }

    openInput() {
        document.getElementById('fileInputInDialog').click();
    }

    selectFile(evt: any) {
        this.dialogRef.close(evt.target.files);
    }
}
